import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/** The section rendered by the Section Rendering API for quick view content. */
const QUICK_VIEW_SECTION_ID = 'quick-view-content';

/** The card whose quick view is currently open, kept so Retry can re-run its load. @type {QuickAddComponent | null} */
let activeQuickAddInstance = null;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** In-flight section requests, keyed by product URL, so a hover prefetch and a click share one request. @type {Map<string, Promise<Element | null>>} */
  #pendingFetches = new Map();
  /** Product URL of the last quick view open attempt, used by retry. @type {string} */
  #lastTriggerUrl = '';
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Warm the section cache before the button is clicked.
    this.addEventListener('pointerenter', this.#prefetchQuickView);
    this.addEventListener('focusin', this.#prefetchQuickView);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.removeEventListener('pointerenter', this.#prefetchQuickView);
    this.removeEventListener('focusin', this.#prefetchQuickView);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#pendingFetches.clear();
  };

  /**
   * Warms the section cache when the shopper points at or focuses the
   * quick-add button. Requests are deduplicated by #getQuickViewContent.
   */
  #prefetchQuickView = () => {
    const url = this.productPageUrl;
    if (!url || this.dataset.usesSellingPlans === 'true') return;
    this.#getQuickViewContent(url);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Builds the quick view section URL for a product.
   * The Section Rendering API returns only the quick view layout, which is
   * far lighter than the full product page.
   * @param {string} productPageUrl - The URL of the product page
   * @returns {string} The section rendering URL, or an empty string
   */
  #getQuickViewUrl(productPageUrl) {
    if (!productPageUrl) return '';

    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', QUICK_VIEW_SECTION_ID);

    const variantId = this.#getSelectedVariantId();
    if (variantId) url.searchParams.set('variant', variantId);

    return url.toString();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    activeQuickAddInstance = this;
    this.#lastTriggerUrl = currentUrl;

    // Open the dialog shell immediately so the modal feels instant; the
    // loading overlay covers the reserved space until the section arrives.
    this.#openQuickAddModal();

    await this.#loadIntoModal(currentUrl);
  };

  /**
   * Loads quick view content for a product URL into the modal, then syncs
   * the card's variant selection. Shows the error panel on failure.
   * @param {string} productPageUrl - The product page URL
   */
  async #loadIntoModal(productPageUrl) {
    // Skip the loading flash when content is available instantly, but still
    // clear any error panel left over from a previous failed attempt.
    this.#setStatus(this.#cachedContent.has(productPageUrl) ? 'content' : 'loading');

    const content = await this.#getQuickViewContent(productPageUrl);

    if (content) {
      await this.updateQuickAddModal(content.cloneNode(true));
      this.#updateVariantPicker(content);
      this.#setStatus('content');
    } else {
      this.#setStatus('error');
    }
  }

  /**
   * Retries the last failed quick view load (wired to the dialog's Retry button).
   */
  reload = async () => {
    const url = this.#lastTriggerUrl || this.productPageUrl;
    if (!url) return;
    await this.#loadIntoModal(url);
  };

  /**
   * Shows or hides the dialog's loading/error overlays.
   * @param {'loading' | 'content' | 'error'} mode - The overlay state
   */
  #setStatus(mode) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const status = dialogComponent.querySelector('[data-quick-view-status]');
    const error = dialogComponent.querySelector('[data-quick-view-error]');

    if (status instanceof HTMLElement) status.hidden = mode !== 'loading';
    if (error instanceof HTMLElement) error.hidden = mode !== 'error';
  }

  /**
   * Loads quick view content for a URL, deduplicating concurrent requests
   * (e.g. a hover prefetch followed by a click) and caching resolved HTML.
   * @param {string} productPageUrl - The product page URL
   * @returns {Promise<Element | null>} A cached/pending element, or null on failure
   */
  #getQuickViewContent(productPageUrl) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    /** @type {Promise<Element | null>} */
    const request = (async () => {
      try {
        const html = await this.fetchProductPage(this.#getQuickViewUrl(productPageUrl));
        const gridElement = html?.querySelector('[data-product-grid-content]');

        if (!gridElement) return null;

        // Cache the element so every open can clone it fresh.
        const content = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(productPageUrl, content);

        return content;
      } catch (error) {
        console.warn('[quick-add] Failed to load quick view:', error);
        return null;
      } finally {
        this.#pendingFetches.delete(productPageUrl);
      }
    })();

    this.#pendingFetches.set(productPageUrl, request);

    return request;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const details = dialogComponent.querySelector('.quick-view__details');
    const media = dialogComponent.querySelector('.quick-view__media');
    // On mobile the whole content column is the scroller.
    const content = dialogComponent.querySelector('.quick-add-modal__content');
    details?.scrollTo({ top: 0, behavior: 'instant' });
    media?.scrollTo({ top: 0, behavior: 'instant' });
    content?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the quick view modal content.
   * The section is responsive by itself, so no DOM restructuring is needed.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleRetryClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleRetryClick);
  }

  /** @param {Event} event - Clicks the dialog's Retry button forward to the active card. */
  #handleRetryClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('[data-quick-view-retry]')) return;
    activeQuickAddInstance?.reload();
  };

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
